<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ListaItens, type SeletorItem } from '@Lar21/imaginii-web-components/vue'
import { encontrarOpcaoDoCatalogo, preservarEstadoSemSelecaoVazia, prepararEstadoSomenteLeitura } from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'
import {
  CHAVE_I18N_CATALOGO_GRUPO_DE_LOCAIS_DE_TRABALHO,
  criarEstadoSeletorCatalogoGrupoDeLocaisDeTrabalho,
  criarPropriedadesPontotelSeletorGrupoDeLocaisDeTrabalho,
  extrairValorDoSeletorCatalogoGrupoDeLocaisDeTrabalho,
  filtrarOpcoesGrupoDeLocaisDeTrabalho,
} from './adaptador-catalogo-grupo-de-locais-de-trabalho'
import { usarCatalogoGrupoDeLocaisDeTrabalho } from './usar-catalogo-grupo-de-locais-de-trabalho'
import type {
  FiltrosCatalogoGrupoDeLocaisDeTrabalho,
  OpcaoGrupoDeLocaisDeTrabalho,
  SelecaoCatalogoGrupoDeLocaisDeTrabalho,
} from '../catalogo-grupo-de-locais-de-trabalho.tipos'

type Props = Readonly<{
  modelValue: SelecaoCatalogoGrupoDeLocaisDeTrabalho
  selecaoMultipla?: boolean
  tamanho?: 'P' | 'M' | 'G'
  filtros?: FiltrosCatalogoGrupoDeLocaisDeTrabalho
  predicado?: (opcao: OpcaoGrupoDeLocaisDeTrabalho) => boolean
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
}>

const props = withDefaults(defineProps<Props>(), {
  selecaoMultipla: false,
  tamanho: 'M',
  desabilitado: false,
  somenteLeitura: false,
  permiteSelecionarTodos: true,
  permiteLimpar: true,
  habilitarDuploClique: false,
  mensagemErro: null,
})
const emit = defineEmits<{
  'update:modelValue': [valor: SelecaoCatalogoGrupoDeLocaisDeTrabalho]
  'filtrando-itens': [filtro: string]
  'duplo-clique': [opcao: OpcaoGrupoDeLocaisDeTrabalho]
  'update:selecionouTodos': [selecionouTodos: boolean]
}>()
const { t } = useI18n()
const catalogo = usarCatalogoGrupoDeLocaisDeTrabalho(computed(() => !props.somenteLeitura))
const opcoes = computed(() => filtrarOpcoesGrupoDeLocaisDeTrabalho(
  catalogo.itens.value,
  props.filtros,
  props.predicado,
))
const estado = computed(() => prepararEstadoSomenteLeitura(
  criarEstadoSeletorCatalogoGrupoDeLocaisDeTrabalho(
    opcoes.value,
    props.modelValue,
    props.selecaoMultipla,
  ),
  props.somenteLeitura,
  props.modelValue,
))
const erro = computed(() => catalogo.erro.value
  ? t(`${CHAVE_I18N_CATALOGO_GRUPO_DE_LOCAIS_DE_TRABALHO}.erroBusca`)
  : props.mensagemErro)
const propriedades = computed(() => (
  criarPropriedadesPontotelSeletorGrupoDeLocaisDeTrabalho({
    traduzir: t,
    ...(props.label !== undefined ? { label: props.label } : {}),
    ...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {}),
    erro: erro.value,
  })
))
const alterarSelecao = (novoEstado: ListaItens): void => { emit(
  'update:modelValue',
  extrairValorDoSeletorCatalogoGrupoDeLocaisDeTrabalho(
    preservarEstadoSemSelecaoVazia(novoEstado, estado.value, props.permiteLimpar),
    opcoes.value,
    props.selecaoMultipla,
  ),
); }
const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoes.value, item)
  if (opcao) emit('duplo-clique', opcao)
}
</script>

<template>
  <PontotelSeletor
    v-bind="propriedades"
    :model-value="estado"
    :tamanho="tamanho"
    :carregar-itens="null"
    :selecao-multipla="selecaoMultipla"
    :desabilitado="desabilitado"
    :modo-exibicao="somenteLeitura"
    :permite-selecionar-todos="permiteSelecionarTodos"
    :habilitar-duplo-clique="habilitarDuploClique"
    :recarregar-todos="!somenteLeitura"
    :recarregando-todos="catalogo.atualizando.value"
    @update:model-value="alterarSelecao"
    @filtrando-itens="emit('filtrando-itens', $event)"
    @duplo-clique="tratarDuploClique"
    @update:selecionou-todos="emit('update:selecionouTodos', $event)"
    @recarregar-todos="catalogo.atualizar"
  />
</template>
